import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, AlertCircle, Droplets, Filter, ShieldCheck, ArrowRight, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import Footer from '../components/Footer';
import UnifiedContactModal from '../components/UnifiedContactModal';

const FloodIrrigationFiltrationPage = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Post-Flood Irrigation Filtration & Dirty Water | CTC Waterworks"
        description="Learn how floods affect irrigation water, ponds, and filtration systems. Practical guidance for growers and irrigation managers on protecting pumps, filters, and emitters after flood events."
        canonicalPath="/flood-irrigation-filtration"
      />
      <Navbar />

      <section className="pt-24 pb-16 bg-gradient-to-br from-green-900 via-emerald-800 to-teal-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYtMi42ODYgNi02cy0yLjY4Ni02LTYtNi02IDIuNjg2LTYgNiAyLjY4NiA2IDYgNnptMCAzNmMzLjMxNCAwIDYtMi42ODYgNi02cy0yLjY4Ni02LTYtNi02IDIuNjg2LTYgNiAyLjY4NiA2IDYgNnpNNTQgMzZjMy4zMTQgMCA2LTIuNjg2IDYtNnMtMi42ODYtNi02LTYtNiAyLjY4Ni02IDYgMi42ODYgNiA2IDZ6TTYgMzZjMy4zMTQgMCA2LTIuNjg2IDYtNnMtMi42ODYtNi02LTYtNiAyLjY4Ni02IDYgMi42ODYgNiA2IDZ6IiBzdHJva2U9IiNmZmYiIHN0cm9rZS13aWR0aD0iMiIvPjwvZz48L3N2Zz4=')] opacity-5"></div>
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center space-x-2 bg-orange-600/20 text-orange-300 px-4 py-2 rounded-full text-sm font-medium mb-6 border border-orange-500/30">
              <AlertCircle size={16} />
              <span>Agriculture & Irrigation</span>
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold mb-6">
              Post-Flood Irrigation Filtration & Dirty Water
            </h1>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Floods and atmospheric rivers leave irrigation water dirtier for seasons to come. Here's how to protect your filters, pumps, and emitters before next year's water turns on.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <button
              onClick={() => setIsContactModalOpen(true)}
              className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-8 py-4 rounded-lg font-semibold hover:from-green-700 hover:to-emerald-700 transition-all duration-300 shadow-lg inline-flex items-center justify-center space-x-3"
            >
              <span>Talk to an Irrigation Filtration Specialist</span>
              <ArrowRight size={20} />
            </button>
            <a
              href="tel:+15099017476"
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-green-900 transition-all duration-300 inline-flex items-center justify-center space-x-2"
            >
              <Phone size={20} />
              <span>Call Now (509) 901-7476</span>
            </a>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Floods Don't Just Hit the Field — They Change Your Water Source
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                When rivers, canals, and ditches flood across farmland, they pick up everything in their path—soil, sand, organics, manure, fertilizers, and debris. Once waters recede, a lot of that material settles into:
              </p>
              <ul className="space-y-3 my-6">
                {[
                  'River and canal beds',
                  'Farm ponds and reservoirs',
                  'Sumps, forebays, and pump bays'
                ].map((item, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={20} />
                    <span className="font-medium">{item}</span>
                  </li>
                ))}
              </ul>
              <p>
                The result is irrigation water that is:
              </p>
              <ul className="space-y-3 my-6">
                {[
                  'Heavier in sediment and turbidity – more silt and fine sand to plug screens and filters',
                  'Loaded with organics and nutrients – a recipe for algae and biofilm when temperatures rise',
                  'Carrying more trash and debris – sticks, leaves, plastics, and crop residue at intakes'
                ].map((item, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <AlertCircle className="text-orange-600 flex-shrink-0 mt-1" size={20} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xl font-semibold text-gray-900 mt-6">
                You may not feel all of it today—but when you start up irrigation in spring, your suction screens, filters, and emitters will.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-8">
              What Growers See After a Big Flood Year
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                {
                  icon: Droplets,
                  title: 'Suction screens on rivers and canals plugging more often'
                },
                {
                  icon: Filter,
                  title: 'Filters going from "weekly rinse" to "daily or multiple-times-a-day"'
                },
                {
                  icon: AlertCircle,
                  title: 'Higher pressure loss across filters even when they appear clean'
                },
                {
                  icon: ShieldCheck,
                  title: 'Emitters and nozzles plugging sooner in the season'
                }
              ].map((problem, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-6 border border-orange-200 flex items-start space-x-4"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <problem.icon className="text-white" size={24} />
                  </div>
                  <p className="text-gray-900 font-medium flex-1">{problem.title}</p>
                </motion.div>
              ))}
            </div>
            <div className="mt-8 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-8 border border-green-200">
              <p className="text-lg text-gray-700 leading-relaxed">
                If you had marginal filtration before, flood-driven dirty water will push it over the edge. The good news: the off-season is the best time to inspect, clean, and upgrade before you're fighting problems in the heat of irrigation season.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">
              Filtration & Intake Protection for Flood-Impacted Irrigation Systems
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-ctc-blue/10 to-blue-50 rounded-xl p-8 border border-ctc-blue/30">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Self-Cleaning Suction Screens</h3>
                <ul className="space-y-3 mb-6">
                  {[
                    'Protect pumps pulling from rivers, canals, and ponds',
                    'Continuously shed debris so crews aren\'t living at the intake location',
                    'Reduce the number of sticks, leaves, and larger debris getting to your filters'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <CheckCircle className="text-ctc-blue flex-shrink-0 mt-0.5" size={18} />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/filtration/suction-screens/self-cleaning"
                  className="inline-flex items-center space-x-2 text-ctc-blue font-semibold hover:text-ctc-blue-dark transition-colors"
                >
                  <span>Learn more about self-cleaning suction screens</span>
                  <ArrowRight size={18} />
                </Link>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-8 border border-green-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Inline Filters Built for Ugly Water</h3>
                <ul className="space-y-3 mb-6">
                  {[
                    'Automated brush & vacuum inline filters to remove sediment and organics before your drip or micros',
                    'Stainless and economy lines sized for higher sediment loading',
                    'Configurable in parallel for systems that have outgrown their old capacity'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/filtration/inline"
                  className="inline-flex items-center space-x-2 text-green-700 font-semibold hover:text-green-800 transition-colors"
                >
                  <span>Explore inline filtration systems</span>
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>
            <div className="mt-8 bg-gradient-to-br from-slate-50 to-gray-100 rounded-xl p-8 border border-gray-200">
              <p className="text-lg text-gray-700 leading-relaxed text-center">
                Our team can review your current suction screen and filter configuration, then recommend adjustments tailored to the dirtier water conditions you'll see after major flood years.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">
              Use the Off-Season to Get Ahead of Next Season's Water
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  step: '1',
                  title: 'Inspect Intakes & Screens',
                  description: 'Pull and inspect suction screens, pump intakes, and pond inlets. Look for dents, coating loss, heavy fouling, and signs of bypassing.'
                },
                {
                  step: '2',
                  title: 'Open Up Filters and Look Inside',
                  description: 'Don\'t just backflush—open housings, inspect screens/elements, and check for accumulated fines and damage.'
                },
                {
                  step: '3',
                  title: 'Right-Size Filtration for Flood-Driven Loads',
                  description: 'If you were barely keeping up before, this is the time to increase screen area, add self-cleaning, or add a pre-filter stage.'
                }
              ].map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-xl p-8 border-2 border-gray-200 shadow-lg"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-green-600 to-emerald-600 rounded-full flex items-center justify-center mb-6 text-white text-2xl font-bold">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                  <p className="text-gray-700">{step.description}</p>
                </motion.div>
              ))}
            </div>
            <div className="mt-8 bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl p-8 text-white text-center">
              <p className="text-xl font-semibold">
                Even a simple inspection and small upgrade now can save you hundreds of labor hours and thousands in crop damage later.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-slate-800 via-slate-900 to-black text-white">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h2 className="text-4xl font-bold mb-6">
              Talk Through Your Irrigation Water & Filtration After the Floods
            </h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Whether you farm in the Yakima Valley, the Skagit and Snohomish valleys, or another flood-prone region, the principles are the same: flood years change how hard your intakes and filters have to work.
            </p>
            <p className="text-lg text-gray-400 mb-8">
              If you're not sure how to size or configure filtration for the next few seasons of dirty water, we're happy to talk it through and offer practical options.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <a
                href="tel:+15099017476"
                className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-8 py-4 rounded-lg font-semibold hover:from-green-700 hover:to-emerald-700 transition-all duration-300 shadow-lg inline-flex items-center justify-center space-x-3"
              >
                <Phone size={20} />
                <span>Call Now (509) 901-7476</span>
              </a>
              <button
                onClick={() => setIsContactModalOpen(true)}
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-slate-900 transition-all duration-300 inline-flex items-center justify-center space-x-2"
              >
                <span>Request a Call Back</span>
                <ArrowRight size={20} />
              </button>
            </div>

            <p className="text-sm text-gray-500 mb-8">
              We prioritize flood and dirty-water irrigation questions and typically return calls within one business day.
            </p>

            <div className="bg-slate-700/50 rounded-xl p-6 border border-slate-600">
              <p className="text-gray-300 mb-2">
                <strong className="text-white">Interested in how water chemistry and pH tie into all this?</strong>
              </p>
              <Link
                to="/sulfur-burners"
                className="inline-flex items-center space-x-2 text-ctc-blue font-semibold hover:text-ctc-blue-light transition-colors"
              >
                <span>Learn about sulfur burners for irrigation water</span>
                <ArrowRight size={18} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />

      <UnifiedContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        productType="filtration"
        formSource="flood_irrigation_filtration_page"
      />
    </div>
  );
};

export default FloodIrrigationFiltrationPage;
